import { StaticInterface } from '../../../helpers/static-interface';
import EmblaCarousel from 'embla-carousel';
import ClassNames from "embla-carousel-class-names";

export interface ITpxClientClientShowcase {
  loadEmbla(parentEl: HTMLElement): void;
}

export interface IStaticClientShowcaseUtils {
  new(...args: any[]): ITpxClientClientShowcase;
  init(): void;
}

/**
 * Knowledge Hero utilities
 * @class
 */
export default class TpxClientShowcaseUtils
  implements StaticInterface<IStaticClientShowcaseUtils, typeof TpxClientShowcaseUtils> {
  constructor(element: HTMLElement) {
    this.loadEmbla(element);
  }

  /**
   * Load Embla
   */
  loadEmbla(parentEl: HTMLElement): void {
    const prefersReducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

    if (!parentEl || prefersReducedMotion) return;

    const carousel = {
      options: {
        loop: false,
        slidesToScroll: 1,
        inViewThreshold: 1,
        draggable: true,
        align: 0,
      },
      components: {
        componentEl: parentEl,
        containerEl: <HTMLElement | null>parentEl.querySelector('[data-component="CarouselContainer"]'),
        buttonEls: <NodeList>parentEl.querySelectorAll('[data-component="Button"]'),
        prevButton: <HTMLElement>parentEl.querySelectorAll('[data-component="Button"]')[0],
        nextButton: <HTMLElement>parentEl.querySelectorAll('[data-component="Button"]')[1],
      }
    }

    if (!carousel.components.containerEl) return;

    const embla = EmblaCarousel(carousel.components.containerEl, carousel.options);
    const prevButton = carousel.components.prevButton;
    const nextButton = carousel.components.nextButton;

    const setupPrevNextBtns = (prevButton: HTMLElement, nextButton: HTMLElement, embla) => {
      prevButton.addEventListener('click', embla.scrollPrev, false);
      nextButton.addEventListener('click', embla.scrollNext, false);
    };

    setupPrevNextBtns(prevButton, nextButton, embla);

    // Update the active state of prev and next btns based on the current slide
    const togglePrevAndNextButtonActiveState = (index: number) => {
      const slideCount = embla.scrollSnapList().length;
      if (index == 0) {
        prevButton.classList.add("is-disabled");
      }
      else if (index == slideCount - 1) {
        nextButton.classList.add("is-disabled");
      }
      else {
        prevButton.classList.remove("is-disabled");
        nextButton.classList.remove("is-disabled");
      }
    }

    const toggleDotSelectedState = (dots: NodeListOf<Element>, indexOfDotToSelect: number) => {
      dots.forEach((dot, i) => {
        if (i == indexOfDotToSelect) { dot.classList.add('is-selected') }
        else { dot.classList.remove('is-selected'); }
      })
    }

    // Update the active dot based on the current slide
    const selectDot = (index: number) => {
      const dots = parentEl.querySelectorAll('[data-component="Dot"]');
      const slideCount = embla.scrollSnapList().length;
      const divisionResult = index / slideCount;

      if (divisionResult <= 0.2) {
        toggleDotSelectedState(dots, 0);
      }
      else if (divisionResult <= 0.4) {
        toggleDotSelectedState(dots, 1);
      }
      else if (divisionResult <= 0.6) {
        toggleDotSelectedState(dots, 2);
      }
      else if (divisionResult <= 0.8) {
        toggleDotSelectedState(dots, 3);
      }
      else {
        toggleDotSelectedState(dots, 4);
      }
    };

    const toggleAndSelect = (index: number) => {
      togglePrevAndNextButtonActiveState(index);
      selectDot(index);
    }

    embla.on('select', () => toggleAndSelect(embla.selectedScrollSnap()));

    // Initial prev/next toggling and dot selection
    toggleAndSelect(embla.selectedScrollSnap());
  }

  /**
   * Create new instances of TpxClientShowcase 
   */
  static init(scope?: HTMLElement) {
    [
      ...(scope || document).querySelectorAll('[data-component="ClientShowcase"]'),
    ].forEach((elem) => {
      new TpxClientShowcaseUtils(elem as HTMLElement);
    });
  }
}
